import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Laptop, LogOut, Monitor, Smartphone, UserCircle } from 'lucide-react';
import { changePassword, getMe, listSessions, revokeSession, updateMe } from '@/lib/api/admin';
import { API_BASE_URL, describeError } from '@/lib/api/client';
import type { SessionSummary } from '@/lib/api/types';
import { formatDateTime, formatRelative, plural } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, Prop } from '@/components/ui/card';
import { Notice, QueryError, Skeleton } from '@/components/ui/feedback';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page';
import { toast } from '@/components/ui/toast';
import { ThemeToggle } from '@/components/shell/theme-toggle';

/** A device icon from a user agent — a hint, not a claim. */
function deviceIcon(ua: string | null) {
  const s = (ua ?? '').toLowerCase();
  if (/iphone|android|mobile/.test(s)) return <Smartphone size={16} />;
  if (/macintosh|windows|linux/.test(s)) return <Laptop size={16} />;
  return <Monitor size={16} />;
}

function describeUa(ua: string | null): string {
  if (!ua) return 'неизвестный клиент';
  const browser = /edg\//i.test(ua) ? 'Edge' : /chrome\//i.test(ua) ? 'Chrome' : /firefox\//i.test(ua) ? 'Firefox' : /safari\//i.test(ua) ? 'Safari' : 'браузер';
  const os = /windows/i.test(ua) ? 'Windows' : /mac os/i.test(ua) ? 'macOS' : /android/i.test(ua) ? 'Android' : /iphone|ipad/i.test(ua) ? 'iOS' : /linux/i.test(ua) ? 'Linux' : '';
  return [browser, os].filter(Boolean).join(' · ');
}

/** The admin's own account: who they are, where they are signed in, and the password. */
export function AccountPage() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: getMe });
  const sessions = useQuery({ queryKey: ['sessions'], queryFn: listSessions });

  const revoke = useMutation({
    mutationFn: (s: SessionSummary) => revokeSession(s.jti),
    onSuccess: (_, s) => { toast('success', 'Сессия завершена', s.deviceName ?? describeUa(s.userAgent)); qc.invalidateQueries({ queryKey: ['sessions'] }); },
    onError: (err) => toast('error', 'Не удалось завершить', describeError(err)),
  });

  const others = (sessions.data ?? []).filter((s) => !s.current);

  return (
    <>
      <PageHeader title="Профиль и сессии" subtitle="Ваш аккаунт администратора платформы, устройства, с которых он вошёл, и пароль." />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader icon={<UserCircle size={18} />} title="Профиль" />
          {me.isPending ? <Skeleton className="h-32" /> : me.isError ? <QueryError error={me.error} /> : <ProfileForm displayName={me.data.displayName} phone={me.data.phone} email={me.data.email} lastLoginAt={me.data.lastLoginAt} />}
          <div className="mt-4 border-t border-line pt-4">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted">Оформление</p>
            <ThemeToggle />
          </div>
        </Card>

        <Card>
          <CardHeader icon={<KeyRound size={18} />} title="Пароль" subtitle="Смена пароля завершает все остальные сессии; эта останется." />
          <PasswordForm />
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            icon={<Laptop size={18} />}
            title="Активные сессии"
            subtitle={sessions.data ? `${plural(sessions.data.length, ['устройство', 'устройства', 'устройств'])} с действующим входом` : undefined}
            action={others.length > 0 && (
              <Button size="sm" variant="outline" icon={<LogOut size={13} />} loading={revoke.isPending} onClick={() => others.forEach((s) => revoke.mutate(s))}>Завершить остальные</Button>
            )}
          />
          {sessions.isPending ? <Skeleton className="h-24" /> : sessions.isError ? <QueryError error={sessions.error} onRetry={() => sessions.refetch()} /> : (
            <ul className="divide-y divide-line">
              {sessions.data.map((s) => (
                <li key={s.jti} className="flex items-center gap-3 py-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-surface-2 text-muted">{deviceIcon(s.userAgent)}</span>
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink">
                      {s.deviceName ?? describeUa(s.userAgent)}
                      {s.current && <Badge tone="ok" dot>это устройство</Badge>}
                    </p>
                    <p className="truncate text-xs text-muted" title={s.userAgent ?? undefined}>
                      {s.deviceName ? describeUa(s.userAgent) : ''}{s.ip ? ` · ${s.ip}` : ''} · вход {formatRelative(s.createdAt)} ({formatDateTime(s.createdAt)})
                    </p>
                  </div>
                  {!s.current && <Button size="sm" variant="ghost" icon={<LogOut size={13} />} loading={revoke.isPending && revoke.variables?.jti === s.jti} onClick={() => revoke.mutate(s)}>Завершить</Button>}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader title="Эта панель" />
          <dl className="divide-y divide-line">
            <Prop label="API" mono>{API_BASE_URL}</Prop>
            <Prop label="Хранение сессии">refresh-токен в localStorage этого браузера, access-токен только в памяти вкладки</Prop>
          </dl>
        </Card>
      </div>
    </>
  );
}

function ProfileForm({ displayName, phone, email, lastLoginAt }: { displayName: string; phone: string | null; email: string; lastLoginAt: string | null }) {
  const qc = useQueryClient();
  const [name, setName] = useState(displayName);
  const [tel, setTel] = useState(phone ?? '');
  const dirty = name.trim() !== displayName || (tel.trim() || null) !== phone;
  const save = useMutation({
    mutationFn: () => updateMe({ displayName: name.trim(), phone: tel.trim() || null }),
    onSuccess: (data) => { qc.setQueryData(['me'], data); toast('success', 'Профиль сохранён'); },
    onError: (err) => toast('error', 'Не удалось сохранить', describeError(err)),
  });
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="space-y-4">
      <Input label="Имя" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input label="Телефон" value={tel} onChange={(e) => setTel(e.target.value)} maxLength={32} placeholder="+998 …" />
        <Input label="E-mail" value={email} disabled hint={lastLoginAt ? `последний вход ${formatRelative(lastLoginAt)}` : undefined} />
      </div>
      <div className="flex justify-end"><Button type="submit" size="sm" disabled={!dirty || !name.trim()} loading={save.isPending}>Сохранить</Button></div>
    </form>
  );
}

function PasswordForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const mismatch = repeat.length > 0 && next !== repeat;
  const change = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => { toast('success', 'Пароль изменён', 'Остальные сессии завершены.'); setCurrent(''); setNext(''); setRepeat(''); },
    onError: (err) => toast('error', 'Пароль не изменён', describeError(err)),
  });
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); if (!mismatch) change.mutate(); }} className="space-y-4">
      <Input label="Текущий пароль" type="password" autoComplete="current-password" required minLength={8} value={current} onChange={(e) => setCurrent(e.target.value)} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input label="Новый пароль" type="password" autoComplete="new-password" required minLength={8} maxLength={128} value={next} onChange={(e) => setNext(e.target.value)} hint="от 8 символов" />
        <Input label="Ещё раз" type="password" autoComplete="new-password" required value={repeat} onChange={(e) => setRepeat(e.target.value)} error={mismatch ? 'Пароли не совпадают' : undefined} />
      </div>
      {change.isError && <Notice tone="danger">{describeError(change.error)}</Notice>}
      <div className="flex justify-end"><Button type="submit" size="sm" disabled={!current || !next || mismatch} loading={change.isPending}>Сменить пароль</Button></div>
    </form>
  );
}
